/**
 * How many failed connections a stream that has never opened is allowed before we conclude there
 * is no agent on this machine.
 *
 * More than one, because "no agent" and "agent not up *yet*" look identical on the first attempt.
 * The tab is routinely there first — the browser restores it at sign-in while the host's `--quiet`
 * autostart is still binding :8780, and `scripts\Update.cmd` leaves the same gap — and Chrome's
 * local-network-access prompt fails the attempt it is asked on and allows the next. Giving up after
 * one meant a transient of a few seconds disabled the bridge for the whole session.
 *
 * Small, because EventSource redials about every three seconds and each attempt on a machine with
 * no agent is a wasted request. Three spans the startup race and still goes quiet in under ten
 * seconds.
 */
export const COLD_ATTEMPT_BUDGET = 3;

/**
 * Whether a dropped bridge connection is worth retrying.
 *
 * `EventSource` reconnects on its own, forever, roughly every three seconds. That is right on the
 * machine running the tray agent — `scripts\Update.cmd` restarts it, and the stream should come
 * back by itself — and wrong everywhere else, because the agent's address is now a baked-in default
 * rather than a build flag. A phone would otherwise spend the whole session dialling a loopback
 * port that nothing is listening on.
 *
 * The distinguishing signal is whether the stream ever opened. It opened and then failed → the
 * agent existed a moment ago → let EventSource keep trying, indefinitely. It has never opened →
 * probably nothing is there → let it try a few times, then stop for good.
 *
 * @param everOpened Whether this stream has ever reached `onopen`.
 * @param coldFailures Consecutive failures *before* any open, this one included.
 *
 * Pure and exported for its own sake: `npm test` has no DOM, so this is the only part of the bridge
 * a test can reach, and it is the rule most likely to be quietly inverted by a later refactor.
 */
export function shouldRetry(everOpened: boolean, coldFailures = 1): boolean {
  return everOpened || coldFailures < COLD_ATTEMPT_BUDGET;
}

/**
 * What a global Read shortcut asks for: Read's `A` or `T` (日本-710), or one of its moves — `↑`/`↓`
 * over the sentences, ⇧⇥/⇥ over the focused sentence's words, `k`/`j` over the dock (日本-729).
 */
export const AGENT_ACTIONS = [
  "add",
  "translate",
  "sentence-prev",
  "sentence-next",
  "word-prev",
  "word-next",
  "result-prev",
  "result-next",
] as const;

export type AgentActionName = (typeof AGENT_ACTIONS)[number];

/**
 * Read an `action` frame's payload, or answer null.
 *
 * **Closed, and an unknown name is dropped** rather than guessed at — a button that did the wrong
 * one of two things is worse than one that did nothing. The names are spelled again in
 * `Nihongonator.Ocr/ReadActions.cs`, with nothing linking the two at build time; its test pins the
 * C# half and `agentBridge.test.ts` pins this one.
 */
export function parseAction(data: unknown): AgentActionName | null {
  if (typeof data !== "object" || data === null) return null;
  const action = (data as { action?: unknown }).action;
  return (AGENT_ACTIONS as readonly unknown[]).includes(action)
    ? (action as AgentActionName)
    : null;
}
